<template>
  <div class="initial-public-offerings-products">
    <div class="initial-public-offerings-products__header-background">
      <section
        :class="{
          'initial-public-offerings-products__header': true,
          'initial-public-offerings-products__header--hover':
            isSearchBarHover && !isSearchBarFocus
        }"
      >
        <div class="initial-public-offerings-products__header-container">
          <SearchBar
            v-model="searchValue"
            class="initial-public-offerings-products__search-bar"
            :dropdown-items="[]"
            placeholder="Buscar produto"
            @mouseenter="isSearchBarHover = true"
            @mouseleave="isSearchBarHover = false"
            @focus="isSearchBarFocus = true"
            @blur="isSearchBarFocus = false"
            @input="onInputChange"
          />
        </div>
      </section>
    </div>
    <LoaderIndicator
      v-if="isLoading"
      class="initial-public-offerings-products__loader"
      size="32"
      with-container
    />
    <HelperViewStatus
      v-else-if="hasError"
      class="initial-public-offerings-products__helper"
      title="Não foi possível carregar as informações"
      description="Aguarde alguns minutos e tente novamente."
      button-label="Tentar novamente"
      status="alert"
      @click="retry"
    />
    <section v-else class="initial-public-offerings-products__table">
      <EmptyHelper
        v-if="isEmpty"
        class="initial-public-offerings-products__helper"
        :type="emptyDataType"
        :search-value="searchValue"
        :filters-count="0"
        @click="handleClear"
      />
      <DataTable
        v-else
        :columns="columnsTable"
        :data="ipos"
        show-divider
        has-interaction
        :status="isSorting ? 'loading' : 'default'"
        @on-sort="onSort"
        @on-row-click="ipo => handleIPOClick(ipo)"
      />
      <LoaderIndicator
        v-if="isScrolling"
        class="initial-public-offerings-products__table-loader"
        size="32"
        with-container
      />
    </section>
    <ButtonIconPrimary
      v-if="hasScrolled"
      :class="scrolledButtonClass"
      label="Ir para o inicio"
      icon="EA0010"
      size="large"
      @click="goToTop"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeMount, onMounted, onUnmounted, ref } from 'vue';
import { debounce } from 'lodash-es';

import {
  SearchBar,
  LoaderIndicator,
  HelperViewStatus,
  DataTable,
  ButtonIconPrimary
} from '@warrenbrasil/nebraska-web';
import { EmptyHelper } from '@/modules/catalog/components';
import { registerObserver } from '@/modules/catalog/views/Products/helpers/registerObserver';
import { columns } from './columns';
import {
  IPagination,
  IProductsParams,
  IInitialPublicOffering,
  getInitialPublicOfferings
} from '@/modules/catalog/services/products';

interface Props {
  isBuilder: boolean;
}
const props = defineProps<Props>();

interface EmitEvents {
  (event: 'select-product', id: string): string;
}
const emit = defineEmits<EmitEvents>();

const ipos = ref<IInitialPublicOffering[]>([]);
const pagination = ref<IPagination>();

const params = ref<IProductsParams>({
  pageIndex: 1,
  pageSize: 10
});

const isLoading = ref(true);
const isScrolling = ref(false);
const isSorting = ref(false);
const hasError = ref(false);
const isSearchBarHover = ref(false);
const isSearchBarFocus = ref(false);

const searchValue = ref('');
const scrollTop = ref(document.documentElement.scrollTop);

const columnsTable = computed(() => {
  return columns(pagination.value!.totalRecords);
});

const hasScrolled = computed(() => {
  return scrollTop.value >= 100;
});

const isEmpty = computed(() => {
  return ipos.value.length === 0;
});

const emptyDataType = computed(() => {
  if (searchValue.value) return 'search';
  return 'empty';
});

const scrolledButtonClass = computed(() => {
  return props.isBuilder
    ? 'initial-public-offerings-products__button builder'
    : 'initial-public-offerings-products__button';
});

const onInputChange = debounce(handleSearch, 500);

async function handleClear() {
  searchValue.value = '';
  handleSearch();
}

async function onSort(index: number, isSort: boolean, value: string) {
  const sortDirection =
    index === -1 ? undefined : isSort ? 'Ascending' : 'Descending';
  const sortField = index === -1 ? undefined : value;

  params.value = {
    ...params.value,
    pageIndex: 1,
    sortField,
    sortDirection
  };

  await fetchInitialPublicOfferings(false, false, true);
  registerObserver(fetchInitialPublicOfferings);
}

async function handleSearch() {
  params.value = {
    ...params.value,
    pageIndex: 1,
    searchTerm: searchValue.value,
    sortField: undefined,
    sortDirection: undefined
  };

  await fetchInitialPublicOfferings();
  registerObserver(fetchInitialPublicOfferings);
}

async function retry() {
  await fetchInitialPublicOfferings();
  registerObserver(fetchInitialPublicOfferings);
}

function goToTop() {
  document.documentElement.scrollTo(0, 0);
}

async function fetchInitialPublicOfferings(
  loading = true,
  scrolling = false,
  sorting = false
) {
  try {
    isLoading.value = loading;
    isScrolling.value = scrolling;
    isSorting.value = sorting;
    hasError.value = false;

    if (isScrolling.value) {
      if (!pagination.value?.nextPage) return;

      params.value = {
        ...params.value,
        pageIndex: pagination.value.nextPage
      };
    }

    const data = await getInitialPublicOfferings(params.value);

    pagination.value = data.pagination;

    if (isScrolling.value) {
      ipos.value.push(...data.items);
      return;
    }

    ipos.value = data.items;
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
    isScrolling.value = false;
    isSorting.value = false;
  }
}

function handleScrollTop() {
  scrollTop.value = document.documentElement.scrollTop;
}

function handleIPOClick(ipo: IInitialPublicOffering) {
  emit('select-product', ipo.productId);

  window.history.pushState(
    {},
    '',
    `/app/#/products-view/catalog/initial-public-offering/${ipo.productId}`
  );
}

onBeforeMount(() => {
  fetchInitialPublicOfferings();
  registerObserver(fetchInitialPublicOfferings);
});

onMounted(() => {
  window.addEventListener('scroll', handleScrollTop);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScrollTop);
});
</script>

<style lang="less" scoped>
.initial-public-offerings-products {
  height: 100%;

  :deep(.input-wrapper) {
    border: none;
    padding: 0;
  }

  :deep(.bottom-helper) {
    display: none;
  }

  :deep(.table__header .table-row) {
    height: 72px;
  }

  :deep(.table__header .table-row .header-cell) {
    .text-p-5();
    font-weight: 600;
    text-align: right;
    padding: @size-spacing-x350 @size-spacing-x400;
  }

  :deep(.header-cell__icon) {
    margin-left: @size-spacing-x300;
  }

  :deep(.table-row .cell p) {
    &:first-of-type {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }

  &__header {
    position: sticky;
    top: 64px;
    left: 0;
    z-index: 20;
    background-color: @background-tertiary;
    width: 100%;

    &--hover {
      background-color: @background-hover;
    }
  }

  &__header-background {
    position: sticky;
    top: 64px;
    left: 0;
    z-index: 20;
    background-color: @background-secondary;

    @media only screen and (max-width: 640px) {
      top: 60px;
    }
  }

  &__header-container {
    padding: 0 @size-spacing-x1100;
    max-width: 1250px;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__search-bar {
    width: 890px;
    max-width: 890px;

    :deep(.trailing-icon) {
      display: none;
    }
  }

  &__helper {
    margin: @size-spacing-x800 auto 0;
    justify-content: center;
  }

  &__table {
    padding: 0 @size-spacing-x1100;
    max-width: 1250px;
    margin: @size-spacing-x800 auto;

    :deep(.table__body .table-row) {
      height: 90px;
    }

    &-loader {
      margin: @size-spacing-x800 auto @size-spacing-x800;
      min-height: 0 !important;
    }
  }

  &__button {
    position: fixed;
    bottom: 32px;
    right: 72px;

    &.builder {
      bottom: 112px;
    }

    @media only screen and (min-width: 1150px) {
      right: calc((100vw - 1008px) / 2);
    }
  }
}
</style>
